/**
 * ViewTailor Curtains Measurement Finder
 * Exact Clone JavaScript from ViewTailorcurtains.com
 * Shopify Theme Section
 *
 * Modified: Added progress indicator updates, fixed step display issue on step 2 change,
 *           enhanced results display to show all selections and inputs,
 *           and changed results details to a table with left-aligned labels and right-aligned values.
 */

(function() {
  'use strict';

  /**
   * ViewTailorCurtainsMeasurementFinder - Main controller class
   */
  class ViewTailorCurtainsMeasurementFinder {
    constructor() {
      this.currentStep = 1;
      this.path = 'yes'; // 'yes' or 'no' - determines which steps to show
      this.formData = {
        headingStyle: '',
        headingStyleName: '',
        rodInstalled: '',
        rodLength: 0,
        windowWidth: 0,
        extraLeft: 6,
        extraRight: 6,
        heightFloor: 0,
        rodMountHeight: 0,
        panelType: '',
        fullness: 2.0,
        finishedLength: ''
      };

      this.init();
    }

    /**
     * Initialize the measurement finder
     */
    init() {
      this.cacheDOM();
      this.bindEvents();
      // Initialize the first step and progress indicator
      this.showStep(1);
    }

    /**
     * Cache DOM elements
     */
    cacheDOM() {
      this.form = document.getElementById('nc-calculator-form');
      this.steps = document.querySelectorAll('.nc-step');
      this.resultSection = document.getElementById('nc-result-section');
      this.recalculateBtn = document.getElementById('nc-recalculate');
      this.progressSteps = document.querySelectorAll('.progress-step');
    }

    /**
     * Bind event listeners
     */
    bindEvents() {
      // Navigation buttons
      document.querySelectorAll('.nc-btn-next').forEach(btn => {
        btn.addEventListener('click', () => this.nextStep());
      });

      document.querySelectorAll('.nc-btn-prev').forEach(btn => {
        btn.addEventListener('click', () => this.prevStep());
      });

      // Calculate buttons
      const calcBtnYes = document.getElementById('nc-calculate-btn');
      if (calcBtnYes) {
        calcBtnYes.addEventListener('click', () => this.calculate());
      }

      const calcBtnNo = document.getElementById('nc-calculate-btn-no');
      if (calcBtnNo) {
        calcBtnNo.addEventListener('click', () => this.calculate());
      }

      // Rod installed change - determines path
      const rodInstalledRadios = document.querySelectorAll('input[name="rod_installed"]');
      rodInstalledRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          this.path = e.target.value;
          this.updatePath(); // This will reset view to step 2
        });
      });

      // Heading style change
      const headingStyleRadios = document.querySelectorAll('input[name="heading_style"]');
      headingStyleRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          this.formData.headingStyle = e.target.value;
          // Update fullness based on heading style
          const fullness = parseFloat(e.target.dataset.fullness) || 2.0;
          this.formData.fullness = fullness;
          // Update fullness radio selection
          const fullnessRadios = document.querySelectorAll('input[name="fullness"]');
          fullnessRadios.forEach(radio => {
            if (parseFloat(radio.value) === fullness) {
              radio.checked = true;
            }
          });
        });
      });

      // Fullness change
      const fullnessRadios = document.querySelectorAll('input[name="fullness"]');
      fullnessRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          this.formData.fullness = parseFloat(e.target.value);
        });
      });

      // Panel type change
      const panelTypeRadios = document.querySelectorAll('input[name="panel_type"]');
      panelTypeRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          this.formData.panelType = e.target.value;
        });
      });

      // Finished length change
      const lengthRadios = document.querySelectorAll('input[name="finished_length"]');
      lengthRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          this.formData.finishedLength = e.target.value;
        });
      });

      // Recalculate button
      if (this.recalculateBtn) {
        this.recalculateBtn.addEventListener('click', () => this.reset());
      }
    }

    /**
     * Update path and ensure step 2 is shown cleanly.
     * This replaces the old updatePath that directly manipulated display.
     */
    updatePath() {
      // When path changes, we reset the view to step 2 to avoid showing conflicting steps.
      this.showStep(2);
    }

    /**
     * Show specific step
     */
    showStep(stepNumber) {
      this.steps.forEach(step => {
        step.classList.remove('active');
        step.style.display = 'none';
      });

      let stepId;
      if (this.path === 'yes') {
        // YES path: 1→2→3yes→4yes→5yes→6yes
        const yesStepMap = {
          1: 'nc-step-1',
          2: 'nc-step-2',
          3: 'nc-step-3-yes',
          4: 'nc-step-4-yes',
          5: 'nc-step-5-yes',
          6: 'nc-step-6-yes'
        };
        stepId = yesStepMap[stepNumber];
      } else {
        // NO path: 1→2→3no→4no→5no→6no→7no→8no→9no
        const noStepMap = {
          1: 'nc-step-1',
          2: 'nc-step-2',
          3: 'nc-step-3-no',
          4: 'nc-step-4-no',
          5: 'nc-step-5-no',
          6: 'nc-step-6-no',
          7: 'nc-step-7-no',
          8: 'nc-step-8-no',
          9: 'nc-step-9-no'
        };
        stepId = noStepMap[stepNumber];
      }

      if (stepId) {
        const step = document.getElementById(stepId);
        if (step) {
          step.classList.add('active');
          step.style.display = 'block';
        }
      }

      this.currentStep = stepNumber;
      this.updateProgressIndicator(stepNumber);
    }

    /**
     * Update the progress indicator based on current step
     */
    updateProgressIndicator(step) {
      if (!this.progressSteps) return;
      this.progressSteps.forEach(el => {
        el.classList.remove('active', 'completed');
        const stepNum = parseInt(el.dataset.step, 10);
        if (stepNum < step) {
          el.classList.add('completed');
        } else if (stepNum === step) {
          el.classList.add('active');
        }
      });
    }

    /**
     * Go to next step
     */
    nextStep() {
      // Validate current step before proceeding
      if (!this.validateCurrentStep()) {
        return;
      }

      // Collect data from current step
      this.collectCurrentStepData();

      // Calculate next step
      const nextStep = this.currentStep + 1;
      const maxStep = this.path === 'yes' ? 6 : 9;

      if (nextStep <= maxStep) {
        this.showStep(nextStep);
      }
    }

    /**
     * Go to previous step
     */
    prevStep() {
      const prevStep = this.currentStep - 1;

      if (prevStep >= 1) {
        this.showStep(prevStep);
      }
    }

    /**
     * Validate current step
     */
    validateCurrentStep() {
      let isValid = true;
      let errorMessage = '';

      switch(this.currentStep) {
        case 1:
          const headingStyle = document.querySelector('input[name="heading_style"]:checked');
          if (!headingStyle) {
            isValid = false;
            errorMessage = 'Please select a heading style';
          }
          break;

        case 2:
          const rodInstalled = document.querySelector('input[name="rod_installed"]:checked');
          if (!rodInstalled) {
            isValid = false;
            errorMessage = 'Please select whether your rod is installed';
          }
          break;

        case 3:
          if (this.path === 'yes') {
            const rodLength = parseFloat(document.getElementById('nc-rod-length')?.value) || 0;
            if (!rodLength || rodLength <= 0) {
              isValid = false;
              errorMessage = 'Please enter your rod length';
            }
          } else {
            const windowWidth = parseFloat(document.getElementById('nc-window-width')?.value) || 0;
            if (!windowWidth || windowWidth <= 0) {
              isValid = false;
              errorMessage = 'Please enter your window width';
            }
          }
          break;

        case 4:
          if (this.path === 'no') {
            // Rod extension - optional with defaults
          } else {
            const panelType = document.querySelector('input[name="panel_type"]:checked');
            if (!panelType) {
              isValid = false;
              errorMessage = 'Please select single or split panels';
            }
          }
          break;

        case 5:
          if (this.path === 'no') {
            const heightFloor = parseFloat(document.getElementById('nc-height-floor')?.value) || 0;
            if (!heightFloor || heightFloor <= 0) {
              isValid = false;
              errorMessage = 'Please enter the height from window to floor';
            }
          } else {
            // For YES path, step 5 is fullness selection, which has a default checked, so no validation needed.
          }
          break;

        case 6:
          if (this.path === 'no') {
            // Rod mount height - optional with defaults
          } else {
            const finishedLength = document.querySelector('input[name="finished_length"]:checked');
            if (!finishedLength) {
              isValid = false;
              errorMessage = 'Please select finished length';
            }
          }
          break;

        case 7:
          if (this.path === 'no') {
            const panelType = document.querySelector('input[name="panel_type"]:checked');
            if (!panelType) {
              isValid = false;
              errorMessage = 'Please select single or split panels';
            }
          }
          break;

        case 8:
          if (this.path === 'no') {
            const fullness = document.querySelector('input[name="fullness"]:checked');
            if (!fullness) {
              isValid = false;
              errorMessage = 'Please select fullness';
            }
          }
          break;

        case 9:
          if (this.path === 'no') {
            const finishedLength = document.querySelector('input[name="finished_length"]:checked');
            if (!finishedLength) {
              isValid = false;
              errorMessage = 'Please select finished length';
            }
          }
          break;
      }

      if (!isValid) {
        alert(errorMessage);
      }

      return isValid;
    }

    /**
     * Collect data from current step
     */
    collectCurrentStepData() {
      switch(this.currentStep) {
        case 1:
          const headingStyle = document.querySelector('input[name="heading_style"]:checked');
          if (headingStyle) {
            this.formData.headingStyle = headingStyle.value;
            const titleEl = headingStyle.closest('.nc-option-card')?.querySelector('.nc-option-title');
            this.formData.headingStyleName = titleEl ? titleEl.textContent : headingStyle.value;
          }
          break;

        case 2:
          const rodInstalled = document.querySelector('input[name="rod_installed"]:checked');
          if (rodInstalled) {
            this.formData.rodInstalled = rodInstalled.value;
            this.path = rodInstalled.value;
          }
          break;

        case 3:
          if (this.path === 'yes') {
            this.formData.rodLength = parseFloat(document.getElementById('nc-rod-length')?.value) || 0;
          } else {
            this.formData.windowWidth = parseFloat(document.getElementById('nc-window-width')?.value) || 0;
          }
          break;

        case 4:
          if (this.path === 'no') {
            this.formData.extraLeft = parseFloat(document.getElementById('nc-extra-left')?.value) || 6;
            this.formData.extraRight = parseFloat(document.getElementById('nc-extra-right')?.value) || 6;
          } else {
            const panelType = document.querySelector('input[name="panel_type"]:checked');
            if (panelType) {
              this.formData.panelType = panelType.value;
            }
          }
          break;

        case 5:
          if (this.path === 'no') {
            this.formData.heightFloor = parseFloat(document.getElementById('nc-height-floor')?.value) || 0;
          } else {
            // YES path step 5: collect fullness
            const fullness = document.querySelector('input[name="fullness"]:checked');
            if (fullness) {
              this.formData.fullness = parseFloat(fullness.value);
            }
          }
          break;

        case 6:
          if (this.path === 'no') {
            this.formData.rodMountHeight = parseFloat(document.getElementById('nc-rod-mount-height')?.value) || 0;
          } else {
            // YES path step 6: collect finished length
            const finishedLength = document.querySelector('input[name="finished_length"]:checked');
            if (finishedLength) {
              this.formData.finishedLength = finishedLength.value;
            }
          }
          break;

        case 7:
          if (this.path === 'no') {
            const panelType = document.querySelector('input[name="panel_type"]:checked');
            if (panelType) {
              this.formData.panelType = panelType.value;
            }
          }
          break;

        case 8:
          if (this.path === 'no') {
            const fullness = document.querySelector('input[name="fullness"]:checked');
            if (fullness) {
              this.formData.fullness = parseFloat(fullness.value);
            }
          }
          break;

        case 9:
          if (this.path === 'no') {
            const finishedLength = document.querySelector('input[name="finished_length"]:checked');
            if (finishedLength) {
              this.formData.finishedLength = finishedLength.value;
            }
          }
          break;
      }
    }

    /**
     * Calculate and display results
     */
    calculate() {
      // Collect all remaining data
      this.collectCurrentStepData();

      // Calculate rod width
      let rodWidth;
      if (this.path === 'yes') {
        rodWidth = this.formData.rodLength;
      } else {
        rodWidth = this.formData.windowWidth + this.formData.extraLeft + this.formData.extraRight;
      }

      // Calculate panel width based on fullness
      const fullness = this.formData.fullness;
      const totalFabricWidth = rodWidth * fullness;
      let panelWidth;

      if (this.formData.panelType === 'split') {
        panelWidth = Math.ceil(totalFabricWidth / 2);
      } else {
        panelWidth = Math.ceil(totalFabricWidth);
      }

      // Calculate finished height
      let finishedHeight;
      if (this.path === 'yes') {
        // Note: In YES path, we don't have actual height measurement. Using rodLength as placeholder.
        // This should be improved based on actual requirements.
        finishedHeight = this.formData.rodLength; 
      } else {
        finishedHeight = this.formData.heightFloor + this.formData.rodMountHeight;
      }

      // Display results with all form data
      this.displayResults({
        panelWidth: panelWidth,
        totalWidth: Math.ceil(totalFabricWidth),
        finishedHeight: Math.ceil(finishedHeight)
      });
    }

    /**
     * Display results with all user selections and inputs in a table
     */
    displayResults(results) {
      // Hide all steps
      this.steps.forEach(step => {
        step.classList.remove('active');
        step.style.display = 'none';
      });

      // Show results section
      if (this.resultSection) {
        this.resultSection.style.display = 'block';

        // Update result
        const sizeElement = document.getElementById('nc-result-size');
        if (sizeElement) {
          sizeElement.textContent = `${results.panelWidth}" Width x ${results.finishedHeight}" Height`;
        }

        // Build table rows
        let tableRows = '';

        // Helper to add a row
        const addRow = (label, value) => {
          tableRows += `<tr><td>${label}</td><td>${value}</td></tr>`;
        };

        // Heading Style
        addRow('Heading Style', this.formData.headingStyleName || this.formData.headingStyle);

        // Rod Installed
        const rodInstalledText = this.formData.rodInstalled === 'yes' ? 'Yes' : (this.formData.rodInstalled === 'no' ? 'No' : '');
        addRow('Rod Installed', rodInstalledText);

        if (this.path === 'yes') {
          // Yes path
          addRow('Rod Length', this.formData.rodLength ? this.formData.rodLength + '"' : '-');
        } else {
          // No path
          addRow('Window Width', this.formData.windowWidth ? this.formData.windowWidth + '"' : '-');
          addRow('Rod Extension Left', this.formData.extraLeft + '"');
          addRow('Rod Extension Right', this.formData.extraRight + '"');
          addRow('Window Top to Floor', this.formData.heightFloor ? this.formData.heightFloor + '"' : '-');
          addRow('Rod Mount Height', this.formData.rodMountHeight ? this.formData.rodMountHeight + '"' : '-');
        }

        // Panel Type
        const panelTypeText = this.formData.panelType === 'single' ? 'Single Panel' : (this.formData.panelType === 'split' ? 'Split Panels' : '');
        addRow('Panel Type', panelTypeText);

        // Fullness
        const fullnessText = this.formData.fullness ? this.formData.fullness + 'X Fullness' : '-';
        addRow('Fullness', fullnessText);

        // Finished Length
        const lengthNames = {
          'above_floor': '1" Above Floor',
          'break_floor': 'Break on Floor',
          'puddle': 'Slight Puddle on Floor'
        };
        const lengthName = lengthNames[this.formData.finishedLength] || this.formData.finishedLength;
        addRow('Finished Length', lengthName);

        // Total Fabric Width
        addRow('Total Fabric Width', results.totalWidth + '"');

        const detailsElement = document.getElementById('nc-result-details');
        if (detailsElement) {
          detailsElement.innerHTML = `<table class="nc-results-table">${tableRows}</table>`;
        }

        // Update progress indicator to show the "E" step (step 10)
        this.updateProgressIndicator(10);

        // Scroll to results
        this.resultSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    /**
     * Reset the calculator
     */
    reset() {
      this.currentStep = 1;
      this.path = 'yes';
      this.formData = {
        headingStyle: '',
        headingStyleName: '',
        rodInstalled: '',
        rodLength: 0,
        windowWidth: 0,
        extraLeft: 6,
        extraRight: 6,
        heightFloor: 0,
        rodMountHeight: 0,
        panelType: '',
        fullness: 2.0,
        finishedLength: ''
      };

      // Reset form
      if (this.form) {
        this.form.reset();
      }

      // Reset radio buttons to defaults
      const headingStyleRadios = document.querySelectorAll('input[name="heading_style"]');
      headingStyleRadios.forEach(radio => {
        if (radio.value === 'pleated') radio.checked = true;
      });

      const rodInstalledRadios = document.querySelectorAll('input[name="rod_installed"]');
      rodInstalledRadios.forEach(radio => {
        if (radio.value === 'yes') radio.checked = true;
      });

      const fullnessRadios = document.querySelectorAll('input[name="fullness"]');
      fullnessRadios.forEach(radio => {
        if (radio.value === '2.0') radio.checked = true;
      });

      const panelTypeRadios = document.querySelectorAll('input[name="panel_type"]');
      panelTypeRadios.forEach(radio => {
        if (radio.value === 'single') radio.checked = true;
      });

      const lengthRadios = document.querySelectorAll('input[name="finished_length"]');
      lengthRadios.forEach(radio => {
        if (radio.value === 'above_floor') radio.checked = true;
      });

      // Hide results
      if (this.resultSection) {
        this.resultSection.style.display = 'none';
      }

      // Reset to step 1
      this.showStep(1);

      // Scroll to top
      const container = document.getElementById('Drapery-measurement-finder');
      if (container) {
        container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    window.ViewTailorCurtainsFinder = new ViewTailorCurtainsMeasurementFinder();
  });

  // Also initialize immediately if DOM is already loaded
  if (document.readyState !== 'loading') {
    window.ViewTailorCurtainsFinder = new ViewTailorCurtainsMeasurementFinder();
  }

})();